import { Button, Table, Tooltip } from 'antd'
import {
  ExclamationCircleOutlined,
  FileSearchOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  EditOutlined,
  AlertOutlined,
  FileExcelOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import queryString from 'query-string'
import { useCallback, useMemo, useState } from 'react'
import { useTheme } from 'react-jss'
import { Link } from 'react-router-dom'
import { useAsync, useIsMobile } from '../../hooks'
import useExportExcel from '../../hooks/useExportExcel'
import useQueryParams from '../../hooks/useQueryParams'
import useLocalStorage from '../../hooks/useLocalStorage'
import {
  useAlerts,
  useAlertsPagination,
  useFetchAlerts,
} from '../../redux/slices/alerts'
import { toPath } from '../../routes'
import { ListError, PageHeader } from '../Common'
import AddNewItemButton from '../Common/AddNewItemButton'
import DiseasesTags from '../Common/DiseasesTags'
import Filters, { FIELDS_OPTIONS } from '../Forms/Filters'
import { useIsAdmin, useIsPractitioner, useCurrentUser, useIsProvider } from '../../redux/slices/session'
import { useSelectedPractice } from '../../context/PracticeContext'
import { formatDOB } from '../../lib/datetime'
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../lib/constants'
import {
  AlertMobileCard,
  AlertMobileDetails,
  EXPAND_COLUMN_WIDTH,
} from './AlertMobileRow'

const DEFAULT_FILTERS = {
  acknowledged: FIELDS_OPTIONS.acknowledged[0].id,
  query: '',
}
const QUERY_PARAMS_SCHEMA = { page: Number, perPage: Number }

// Hoisted out of the component so the expanded row is not a nested component
// (react/no-unstable-nested-components) and keeps a stable identity.
const renderMobileDetails = alert => <AlertMobileDetails alert={alert} />

const MOBILE_EXPANDABLE = {
  columnWidth: EXPAND_COLUMN_WIDTH,
  expandedRowRender: renderMobileDetails,
}

export default function AlertsList() {
  const { brandDanger, brandSuccess } = useTheme()
  const isAdmin = useIsAdmin()
  const isMobile = useIsMobile()
  const isPractitioner = useIsPractitioner()
  const user = useCurrentUser()
  const isProvider = useIsProvider()
  const { selectedPracticeId } = useSelectedPractice()
  const [hasInteracted, setHasInteracted] = useState(false)

  const [conditions, setConditions] = useQueryParams({
    initialValues: {
      ...DEFAULT_FILTERS,
      // Seed practiceId so the first fetch is already scoped to the selected practice (#937).
      practiceId: isAdmin ? selectedPracticeId || undefined : user.practice.id,
      medicalProviderId: isProvider && !hasInteracted ? user?.id : undefined,
    },
    schema: QUERY_PARAMS_SCHEMA,
  })
  // eslint-disable-next-line no-unused-vars
  const [localStoragePageSize, setPageSize] = useLocalStorage(
    'pageSize',
    PAGE_SIZE,
  )
  const pageSize = conditions.perPage || localStoragePageSize
  const fromPracticeParam = useMemo(
    () => (conditions.practiceId ? '?fromPractice=true' : ''),
    [conditions],
  )

  const columns = useMemo(
    () => [
      {
        title: <ExclamationCircleOutlined style={{ color: brandDanger }} />,
        dataIndex: 'critical',
        key: 'critical',
        align: 'center',
        render(_text, { critical }) {
          return (
            critical && (
              <Tooltip title="Requires Attention">
                <ExclamationCircleOutlined style={{ color: brandDanger }} />
              </Tooltip>
            )
          )
        },
      },
      {
        title: 'Full Name',
        key: 'fullName',
        render(_text, alert) {
          return (
            <Link to={toPath.patients.patient({ id: alert.patientSummary.id })}>
              {alert.patientSummary.lastName}, {alert.patientSummary.firstName}
            </Link>
          )
        },
      },
      {
        title: 'MRN',
        dataIndex: ['patientSummary', 'practiceMrn'],
        key: 'MRN',
      },
      {
        title: 'DOB',
        dataIndex: ['patientSummary', 'birthDate'],
        key: 'dob',
        render: formatDOB,
      },
      {
        title: 'Stroke Prevention',
        key: 'diseases',
        render(text, alert) {
          const { patientSummary } = alert
          return <DiseasesTags patientSummary={patientSummary} />
        },
      },
      {
        title: 'Device Type',
        dataIndex: ['device', 'deviceModel', 'deviceType', 'name'],
        key: 'deviceType',
      },
      {
        title: 'Created At',
        key: 'createdAt',
        dataIndex: 'createdAt',
        render(text) {
          return dayjs(text).tz().calendar(null, {
            sameElse: 'MMM D, YYYY h:mm A',
          })
        },
      },
      {
        align: 'center',
        title: 'Acknowledged',
        key: 'acknowledged',
        dataIndex: 'acknowledgedAt',

        render: acknowledgedAtText =>
          acknowledgedAtText ? (
            <CheckCircleFilled style={{ color: brandSuccess }} />
          ) : (
            <CloseCircleFilled style={{ color: brandDanger }} />
          ),
      },
      {
        title: 'Actions',
        key: 'actions',
        render(text, alert) {
          return (
            <div style={{ display: 'flex' }}>
              <Link
                to={`${toPath.alerts.alert({
                  id: alert.id,
                })}${fromPracticeParam}`}
              >
                <Button type="link" icon={<FileSearchOutlined />} />
              </Link>
              <Link
                to={toPath.alerts.alert.edit({ id: alert.id })}
                style={{ display: 'block' }}
              >
                <Button type="link" icon={<EditOutlined />} />
              </Link>
            </div>
          )
        },
      },
    ],
    [brandDanger, brandSuccess, fromPracticeParam],
  )

  // On mobile the wide table collapses to one card per alert: severity, patient
  // and acknowledged state stay visible, the identifying fields expand behind a
  // per-row toggle.
  const mobileColumns = useMemo(
    () => [
      {
        title: 'Alert',
        key: 'alert',
        render(_, alert) {
          return (
            <AlertMobileCard
              alert={alert}
              fromPracticeParam={fromPracticeParam}
            />
          )
        },
      },
    ],
    [fromPracticeParam],
  )

  const { pending, error, clearError } = useAsync(
    useFetchAlerts({ ...conditions, perPage: pageSize }),
  )

  const handlePaginationChange = useCallback(
    (newPage, newPageSize) => {
      if (newPageSize !== Number(pageSize)) {
        setPageSize(newPageSize)
        setConditions({ ...conditions, page: newPage, perPage: newPageSize })
      } else {
        setConditions({ ...conditions, page: newPage })
      }
    },
    [pageSize, conditions, setConditions, setPageSize],
  )

  const handleSubmit = useCallback(
    ({
      practiceId,
      acknowledged,
      critical,
      medicalProviderId,
      deviceTypeId,
      officeLocationId,
      query,
    }) => {
      setHasInteracted(true)
      const payload = {
        page: 1,
        practiceId: practiceId || undefined,
        acknowledged,
        critical: critical ? 'true' : undefined,
        medicalProviderId,
        deviceTypeId,
        query,
      }
      if (practiceId === conditions.practiceId)
        payload.officeLocationId = officeLocationId
      setConditions(payload)
    },
    [conditions.practiceId, setConditions],
  )

  const { total } = useAlertsPagination()
  const alerts = useAlerts()

  const exportUrl = useMemo(
    () =>
      queryString.stringifyUrl(
        {
          url: '/alerts.xlsx',
          query: {
            query: conditions.query,
            acknowledged: conditions.acknowledged,
            critical: conditions.critical,
            practice_id: conditions.practiceId,
            device_type_id: conditions.deviceTypeId,
            medical_provider_id: conditions.medicalProviderId,
            office_location_id: conditions.officeLocationId,
          },
        },
        { skipNull: true, skipEmptyString: true },
      ),
    [conditions],
  )

  const [exportingExcel, exportExcel] = useExportExcel(exportUrl)

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
      <PageHeader
        title="Alerts"
        extra={
          isAdmin && (
            <AddNewItemButton
              itemName="alert"
              path={toPath.alerts.new()}
              icon={<AlertOutlined />}
            />
          )
        }
      />
      {(isAdmin || isPractitioner) && (
        <Filters
          currentValues={conditions}
          onSubmit={handleSubmit}
          showPractice={isAdmin}
          showAcknowledged
          showRequiresAttention
          showDeviceType
          showMedicalProvider
          showOfficeLocation
          showSearch
          placeholders={{
            query: 'Search by name, serial number or practice MRN',
          }}
        />
      )}
      <Table
        size={isMobile ? 'small' : undefined}
        columns={isMobile ? mobileColumns : columns}
        showHeader={!isMobile}
        expandable={isMobile ? MOBILE_EXPANDABLE : undefined}
        dataSource={alerts}
        loading={pending}
        pagination={{
          current: conditions.page || 1,
          pageSize: Number(pageSize),
          onChange: handlePaginationChange,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          total,
        }}
        rowKey="id"
      />
      {/* Gate matches the Filters block: only roles that can filter the */}
      {/* list (admins, practitioners) can export it. */}
      {(isAdmin || isPractitioner) && Boolean(alerts.length) && (
        <div>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </div>
  )
}
